<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else class="flex flex-col items-center p-4">
    <div class="w-full md:w-8/12">
      <!-- Header with Edit Button -->
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold">{{ t('accountDetails') }}</h1>
        <div class="flex gap-2">
          <horizontal-button
            v-if="$can('update', '/accounting/accounts')"
            :title="$t('views.common.listView.edit')"
            faIcon="edit"
            @click.prevent="goToEdit"
          />
          <horizontal-button
            :title="t('back')"
            faIcon="arrow-left"
            @click.prevent="$router.push($Route.ACCOUNTING_ACCOUNTS)"
          />
        </div>
      </div>

      <!-- Account Information Card -->
      <el-card class="mb-4">
        <template #header>
          <div class="card-header">
            <span class="text-lg font-semibold">{{ t('basicInformation') }}</span>
          </div>
        </template>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- English Name -->
          <div class="field-group">
            <label class="field-label">{{ t('enName') }}</label>
            <p class="field-value">{{ account.enName }}</p>
          </div>

          <!-- Arabic Name -->
          <div class="field-group">
            <label class="field-label">{{ t('arName') }}</label>
            <p class="field-value">{{ account.arName }}</p>
          </div>

          <!-- Code -->
          <div class="field-group">
            <label class="field-label">{{ t('code') }}</label>
            <p class="field-value">
              <span v-if="account.code" class="code-badge">{{ account.code }}</span>
              <span v-else class="text-gray-400">-</span>
            </p>
          </div>

          <!-- Account Category -->
          <div class="field-group">
            <label class="field-label">{{ t('accountCategory') }}</label>
            <p class="field-value">
              <el-tag v-if="account.accountCategory" type="info">
                {{ getCategoryLabel(account.accountCategory) }}
              </el-tag>
              <span v-else class="text-gray-400">-</span>
            </p>
          </div>

          <!-- Parent Account -->
          <div class="field-group">
            <label class="field-label">{{ t('parentAccount') }}</label>
            <p class="field-value">
              <el-link
                v-if="account.parentAccount"
                type="primary"
                @click="viewParentAccount"
              >
                {{ $ctx.locale === 'en' ? account.parentAccount.enName : account.parentAccount.arName }}
              </el-link>
              <span v-else class="text-gray-400">{{ t('noParent') }}</span>
            </p>
          </div>

          <!-- Subcategory -->
          <div class="field-group">
            <label class="field-label">{{ t('subcategory') }}</label>
            <p class="field-value">
              <el-tag v-if="account.accountSubcategory" type="success">
                {{ getSubcategoryLabel(account.accountSubcategory) }}
              </el-tag>
              <span v-else class="text-gray-400">-</span>
            </p>
          </div>

          <!-- Location -->
          <div class="field-group">
            <label class="field-label">{{ t('location') }}</label>
            <p class="field-value">
              <span v-if="location">
                {{ $ctx.locale === 'en' ? location.enName : location.arName }}
              </span>
              <span v-else class="text-gray-400">-</span>
            </p>
          </div>

          <!-- System Account -->
          <div class="field-group">
            <label class="field-label">{{ t('isSystemAccount') }}</label>
            <p class="field-value">
              <el-tag :type="account.isSystemAccount ? 'warning' : 'info'">
                {{ account.isSystemAccount ? t('yes') : t('no') }}
              </el-tag>
            </p>
          </div>


          <!-- Balance -->
          <div class="field-group">
            <label class="field-label">{{ t('balance') }}</label>
            <p class="field-value">
              <el-tag :type="account.balance === 'debit' ? 'primary' : 'success'">
                {{ t(account.balance) }}
              </el-tag>
            </p>
          </div>

          <!-- Shared Account -->
          <div class="field-group">
            <label class="field-label">{{ t('isSharedAccount') }}</label>
            <p class="field-value">
              <el-tag :type="account?.accountTemplate?.isSharedAccount ? 'success' : 'info'">
                {{ account?.accountTemplate?.isSharedAccount ? t('yes') : t('no') }}
              </el-tag>
            </p>
          </div>

          <!-- Only for Current Location -->
          <div class="field-group">
            <label class="field-label">{{ t('isOnlyForCurrentLocation') }}</label>
            <p class="field-value">
              <el-tag :type="account.accountTemplate?.isOnlyForCurrentLocation ? 'warning' : 'info'">
                {{ account.accountTemplate?.isOnlyForCurrentLocation ? t('yes') : t('no') }}
              </el-tag>
            </p>
          </div>

          <!-- Cashier Register -->
          <div class="field-group">
            <label class="field-label">{{ t('isCashierRegister') }}</label>
            <p class="field-value">
              <el-tag :type="account.isCashierRegister ? 'success' : 'info'">
                {{ account.isCashierRegister ? t('yes') : t('no') }}
              </el-tag>
            </p>
          </div>

          <!-- Transactional -->
          <div class="field-group">
            <label class="field-label">{{ t('isTransactional') }}</label>
            <p class="field-value">
              <el-tag :type="account.isTransactional ? 'success' : 'info'">
                {{ account.isTransactional ? t('yes') : t('no') }}
              </el-tag>
            </p>
          </div>

          <!-- English Description -->
          <div class="field-group md:col-span-2">
            <label class="field-label">{{ t('enDescription') }}</label>
            <p class="field-value">{{ account.enDescription || '-' }}</p>
          </div>

          <!-- Arabic Description -->
          <div class="field-group md:col-span-2">
            <label class="field-label">{{ t('arDescription') }}</label>
            <p class="field-value">{{ account.arDescription || '-' }}</p>
          </div>
        </div>
      </el-card>

      <!-- Metadata Card -->
      <el-card>
        <template #header>
          <div class="card-header">
            <span class="text-lg font-semibold">{{ t('metadata') }}</span>
          </div>
        </template>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Created At -->
          <div class="field-group">
            <label class="field-label">{{ $t('models.common.createdAt') }}</label>
            <p class="field-value" dir="ltr">{{ parseFormat(account.createdAt) }}</p>
          </div>

          <!-- Updated At -->
          <div class="field-group">
            <label class="field-label">{{ $t('models.common.updatedAt') }}</label>
            <p class="field-value" dir="ltr">{{ parseFormat(account.updatedAt) }}</p>
          </div>

          <!-- External ID -->
          <div class="field-group" v-if="account.externalID">
            <label class="field-label">{{ t('externalID') }}</label>
            <p class="field-value text-sm">{{ account.externalID }}</p>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, ref } from 'vue'

import { useHTTP } from '@/plugins/http'

import { Account, accountI18nMessages } from '@/models/accounting/Account'
import Location from '@/models/company/Location'

import { parseFormat } from '@/utils/date'

// components
import AlertBadge from '@/components/AlertBadge.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

export default defineComponent({
  name: 'account-view',
  components: {
    AlertBadge,
    LoadingSpinner,
    HorizontalButton
  },
  setup() {
    const account = ref(new Account())
    const location = ref<Location | null>(null)
    const http = useHTTP()
    const loading = ref(true)
    const error = ref({ title: '', body: '' })
    const route = useRoute()
    const router = useRouter()

    const { t } = useI18n({
      messages: {
        en: {
          ...accountI18nMessages.en,
          accountDetails: 'Account Details',
          basicInformation: 'Basic Information',
          relationshipInfo: 'Relationships',
          metadata: 'Metadata',
          back: 'Back to Accounts',
          noParent: 'No parent account',
          yes: 'Yes',
          no: 'No',
          location: 'Location',
          externalID: 'External ID',
          subcategory: 'Subcategory',
          isSharedAccount: 'Shared Account',
          isOnlyForCurrentLocation: 'Only for Current Location',
          assets: 'Assets',
          liabilities: 'Liabilities',
          equity: 'Equity',
          revenue: 'Revenue',
          expenses: 'Expenses',
          "current-assets": "Current Asset",
          "fixed-assets": "Fixed Asset (PPE)",
          "employee-receivable": "Employee Receivable",
          "contra-assets": "Contra-Asset",
          "current-liabilities": "Current Liability",
          "accrued-expense": "Accrued Expense",
          "long-term-liabilities": "Long-term Liability",
          "owner-equity": "Owner Equity",
          "contra-equity": "Contra-Equity",
          "operating-revenue": "Operating Revenue",
          "contra-revenue": "Contra-Revenue",
          "cogs": "COGS",
          "adjustment": "Adjustment",
          "write-off": "Write-off",
          "contra-cogs": "Contra-COGS",
          "payroll": "Payroll",
          "operating-expenses": "Operating Expenses",
          "selling-expenses": "Selling Expense",
          "depreciation": "Depreciation",
          "other-expenses": "Other Expenses",
        },
        ar: {
          ...accountI18nMessages.ar,
          accountDetails: 'تفاصيل الحساب',
          basicInformation: 'المعلومات الأساسية',
          relationshipInfo: 'العلاقات',
          metadata: 'البيانات الوصفية',
          back: 'العودة للحسابات',
          noParent: 'لا يوجد حساب أب',
          yes: 'نعم',
          no: 'لا',
          location: 'الموقع',
          externalID: 'المعرف',
          subcategory: 'الفئة الفرعية',
          isSharedAccount: 'حساب مشترك',
          isOnlyForCurrentLocation: 'للموقع الحالي فقط',
          assets: 'الممتلكات',
          liabilities: 'الديون',
          equity: 'حقوق الملكية',
          revenue: 'الوارد',
          expenses: 'المصاريف',
          "current-assets": "أصول متداولة",
          "fixed-assets": "أصول ثابتة",
          "employee-receivable": "سلفة للموظفين",
          "contra-assets": "أصول مقابلة",
          "current-liabilities": "خصوم متداولة",
          "accrued-expense": "مصاريف مستحقة",
          "long-term-liabilities": "خصوم طويلة الأجل",
          "owner-equity": "رأس مال المالك",
          "contra-equity": "حقوق ملكية مقابلة",
          "owner-equity": "حقوق ملكية",
          "cogs": "تكلفة المبيعات",
          "adjustment": "تعديلات",
          "write-off": "شطب",
          "contra-cogs": "تكلفة مبيعات مقابلة",
          "payroll": "رواتب وأجور",
          "operating-revenue": "إيرادات تشغيلية",
          "contra-revenue": "إيرادات مقابلة",
          "operating-expenses": "مصاريف تشغيلية",
          "selling-expenses": "مصاريف بيع",
          "depreciation": "اندثار",
          "other-expenses": "مصاريف أخرى"
        }
      },
      unescape: 'global'
    })

    onBeforeMount(async () => {
      const accountUrl = `${Account.ENDPOINT}/${route.params.id}`

      try {
        const resp = await http.get<Account>(accountUrl)
        account.value = Account.from(resp)

        // Fetch location if account has one
        if (account.value.referenceableType === 'location.Location' && account.value.referenceableID) {
          try {
            const locationResp = await http.get<Location>(`${Location.ENDPOINT}/${account.value.referenceableID}`)
            location.value = Location.from(locationResp)
          } catch (err) {
            // Location fetch failed, but continue anyway
            console.error('Failed to fetch location:', err)
          }
        }
      } catch (errorResp: any) {
        error.value.title = errorResp.title || 'Error'
        error.value.body = errorResp.body || 'Failed to load account'
      }

      loading.value = false
    })

    const goToEdit = () => {
      router.push(`/accounting/accounts/account/${account.value.id}`)
    }

    const viewParentAccount = () => {
      if (account.value.parentAccount?.id) {
        router.push(`/accounting/accounts/view/${account.value.parentAccount.id}`)
      }
    }

    const getCategoryLabel = (category: string) => {
      const categoryMap: Record<string, string> = {
        'assets': t('assets'),
        'liabilities': t('liabilities'),
        'equity': t('equity'),
        'revenue': t('revenue'),
        'expenses': t('expenses')
      }
      return categoryMap[category] || category
    }

    const getSubcategoryLabel = (subcategory: string) => {
      return t(subcategory) || subcategory
    }

    return {
      account,
      location,
      error,
      loading,
      goToEdit,
      viewParentAccount,
      getCategoryLabel,
      getSubcategoryLabel,
      parseFormat,
      t
    }
  }
})
</script>

<style scoped>
.field-group {
  @apply mb-4;
}

.field-label {
  @apply block text-sm font-semibold text-gray-600 mb-1;
}

.field-value {
  @apply text-base text-gray-900;
}

.code-badge {
  @apply inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm;
}

.card-header {
  @apply flex items-center justify-between;
}

:deep(.el-card__header) {
  @apply bg-gray-50;
}
</style>
